import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { villageInfants, recentDonors, donationStats } from "@/mocks/donation";

export default function DonationSection() {
  const navigate = useNavigate();
  const [tickerIdx, setTickerIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setTickerIdx((p) => (p + 1) % recentDonors.length), 3500);
    return () => clearInterval(t);
  }, []);

  const displayInfants = villageInfants.slice(0, 4);

  return (
    <section className="py-14 bg-gradient-to-b from-white to-[#fff5f5]">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-6 bg-red-500 rounded-full"></div>
              <span className="text-red-500 text-sm font-medium">我们是一个大家庭 · 爱心无国界</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              爱心村童捐赠计划
            </h2>
            <p className="text-gray-500 text-sm mt-1 max-w-xl">
              欢迎彩票中奖者、富有爱心的成功人士为偏远山村婴儿送去奶粉、图书和爱心礼物<br />
              <span className="text-red-500 font-medium">这些孩子将用一生感谢您的善良</span>
            </p>
          </div>
          <button
            onClick={() => navigate("/donation")}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-all whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-heart-line"></i> 立即捐赠
          </button>
        </div>

        {/* Live ticker */}
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-2.5 flex items-center gap-3 mb-6">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></div>
          <span className="text-gray-600 text-sm">
            <strong className="text-red-600">{recentDonors[tickerIdx].name}</strong>
            {" "}刚刚捐赠了{" "}
            <strong className="text-red-600">¥{recentDonors[tickerIdx].amount.toLocaleString()}</strong>
            {" "}— {recentDonors[tickerIdx].gift}
          </span>
          <span className="text-gray-400 text-xs ml-auto whitespace-nowrap">{recentDonors[tickerIdx].time}</span>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { label: "爱心捐赠者", value: `${donationStats.totalDonors.toLocaleString()}+`, icon: "ri-user-heart-line", color: "text-red-500 bg-red-50" },
            { label: "累计捐赠额", value: `¥${(donationStats.totalAmount / 10000).toFixed(0)}万`, icon: "ri-money-cny-circle-line", color: "text-amber-500 bg-amber-50" },
            { label: "受助婴儿", value: `${donationStats.infantsHelped}+`, icon: "ri-emotion-happy-line", color: "text-pink-500 bg-pink-50" },
            { label: "礼物已送达", value: `${donationStats.giftsDelivered.toLocaleString()}+`, icon: "ri-gift-line", color: "text-green-500 bg-green-50" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3">
              <div className={`w-10 h-10 flex items-center justify-center ${s.color} rounded-xl flex-shrink-0`}>
                <i className={`${s.icon} text-xl`}></i>
              </div>
              <div>
                <div className="font-bold text-gray-900 text-lg leading-tight">{s.value}</div>
                <div className="text-gray-500 text-xs">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Infant cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {displayInfants.map((infant) => {
            const progress = Math.round((infant.raisedAmount / infant.goalAmount) * 100);
            return (
              <div
                key={infant.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-red-200 transition-all cursor-pointer group"
                onClick={() => navigate("/donation")}
              >
                <div className="relative overflow-hidden" style={{ height: "180px" }}>
                  <img
                    src={infant.image}
                    alt={infant.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {infant.status === "urgent" && (
                    <div className="absolute top-2 left-2">
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium animate-pulse">紧急</span>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                    <div className="text-white font-bold">{infant.name} · {infant.age}</div>
                    <div className="text-white/70 text-xs">{infant.province}</div>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-gray-500 text-xs leading-relaxed mb-2 line-clamp-2">{infant.story}</p>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-1">
                    <div
                      className="h-full bg-gradient-to-r from-red-400 to-red-600 rounded-full"
                      style={{ width: `${Math.min(progress, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-red-500 font-medium">{progress}%</span>
                    <span className="text-gray-400">{infant.donors}人支持</span>
                  </div>
                  <button
                    className="w-full mt-2 bg-red-50 hover:bg-red-100 text-red-600 text-xs py-1.5 rounded-lg transition-colors cursor-pointer font-medium whitespace-nowrap"
                    onClick={(e) => { e.stopPropagation(); navigate("/donation"); }}
                  >
                    <i className="ri-heart-line mr-1"></i>为{infant.name}捐赠
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA banner */}
        <div className="relative rounded-2xl overflow-hidden" style={{ minHeight: "160px" }}>
          <img
            src="https://public.readdy.ai/ai/img_res/bc97f56e4cbbe70c8485f694dbf66392.jpg"
            alt="爱心捐赠"
            className="w-full h-full object-cover object-top absolute inset-0"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>
          <div className="relative z-10 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 h-full">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-home-heart-line text-yellow-300 text-xl"></i>
                <span className="text-yellow-300 text-sm font-medium">我们是一个大家庭</span>
              </div>
              <h3 className="text-white text-xl font-bold mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                您的爱心，是孩子一生中最美好的礼物
              </h3>
              <p className="text-gray-200 text-sm">
                捐赠后收到收据 + 婴儿收到礼物的照片 · 感恩父母朋友社会 · 关爱小朋友和长者
              </p>
            </div>
            <button
              onClick={() => navigate("/donation")}
              className="bg-yellow-400 hover:bg-yellow-500 text-red-900 px-6 py-3 rounded-full font-bold cursor-pointer transition-colors whitespace-nowrap flex-shrink-0 text-sm"
            >
              <i className="ri-heart-fill mr-2"></i>加入爱心大家庭
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
